import React, { useState } from 'react';
import Modal from '../../shared/components/Modal';
import Field from '../../shared/components/Field';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { sendTelegramNotification, formatDateFR } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';

export default function ChequeForm({ cheque, settings, onClose }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const { add, update } = useCollection('cheques');

  const [data, setData] = useState(cheque || {
    type: 'received',          // 'received' (mn client) | 'paid' (l fournisseur)
    chequeNumber: '',
    amount: 0,
    bankName: '',
    accountHolder: '',         // Smiya dyal sahib chèque
    dueDate: new Date().toISOString().split('T')[0],
    issueDate: new Date().toISOString().split('T')[0],
    status: 'pending',         // pending | cashed | bounced | cancelled
    note: '',
    relatedClient: '',         // ID dyal client (optionnel)
    relatedCar: ''             // ID dyal voiture (optionnel)
  });

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!data.chequeNumber || !data.amount || !data.dueDate) {
      return alert(isAr ? '⚠️ الرقم، المبلغ والتاريخ مطلوبة' : '⚠️ N°, montant et date requis');
    }
    
    const payload = { ...data, amount: parseFloat(data.amount) || 0 };
    
    try {
      if (cheque?.id) {
        await update(cheque.id, payload);
      } else {
        payload.createdAt = new Date().toISOString();
        await add(payload);
        
        // Send Telegram notification
        await sendTelegramNotification(
          buildTelegramMessage('new_cheque', {
            type: data.type, number: data.chequeNumber, amount: data.amount,
            currency: settings.currency || 'MAD', bank: data.bankName,
            holder: data.accountHolder, dueDate: formatDateFR(data.dueDate)
          }),
          'new_cheque'
        );
      }
      onClose();
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  return (
    <Modal onClose={onClose} title={cheque ? (isAr ? '✏️ تعديل الشيك' : '✏️ Modifier le chèque') : (isAr ? '➕ شيك جديد' : '➕ Nouveau chèque')}>
      <form onSubmit={submit}>
        {/* Type selector (received / paid) */}
        <div className="mb-4">
          <label className="block text-sm font-bold text-gray-700 mb-2">
            {isAr ? '📋 نوع الشيك' : '📋 Type de chèque'}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setData({ ...data, type: 'received' })}
              className={`p-3 rounded-lg border-2 transition ${
                data.type === 'received' 
                  ? 'bg-green-50 border-green-500 shadow-sm' 
                  : 'bg-white border-gray-200 hover:border-green-300'
              }`}
            >
              <div className="text-2xl mb-1">📥</div>
              <div className="text-sm font-bold">{isAr ? 'مستلم' : 'Reçu'}</div>
              <div className="text-[10px] text-gray-500">{isAr ? 'من العميل' : 'Du client'}</div>
            </button>
            <button
              type="button"
              onClick={() => setData({ ...data, type: 'paid' })}
              className={`p-3 rounded-lg border-2 transition ${
                data.type === 'paid' 
                  ? 'bg-red-50 border-red-500 shadow-sm' 
                  : 'bg-white border-gray-200 hover:border-red-300'
              }`}
            >
              <div className="text-2xl mb-1">📤</div>
              <div className="text-sm font-bold">{isAr ? 'صادر' : 'Émis'}</div>
              <div className="text-[10px] text-gray-500">{isAr ? 'للمورد' : 'Au fournisseur'}</div>
            </button>
          </div>
        </div>
        
        {/* Main fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field
            label={'🔢 ' + (isAr ? 'رقم الشيك' : 'N° du chèque')}
            value={data.chequeNumber}
            onChange={v => setData({ ...data, chequeNumber: v })}
            placeholder="1234567"
          />
          <Field
            label={'💰 ' + (isAr ? 'المبلغ' : 'Montant')}
            type="number"
            value={data.amount}
            onChange={v => setData({ ...data, amount: v })}
            placeholder="5000"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field
            label={'🏦 ' + (isAr ? 'البنك' : 'Banque')}
            value={data.bankName}
            onChange={v => setData({ ...data, bankName: v })}
            placeholder="Attijariwafa, BMCE, ..."
          />
          <Field
            label={'👤 ' + (data.type === 'received' 
              ? (isAr ? 'اسم العميل' : 'Nom du client')
              : (isAr ? 'المستفيد' : 'Bénéficiaire'))}
            value={data.accountHolder}
            onChange={v => setData({ ...data, accountHolder: v })}
            placeholder="Ahmed Benali"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field
            label={'📅 ' + (isAr ? 'تاريخ الإصدار' : "Date d'émission")}
            type="date"
            value={data.issueDate}
            onChange={v => setData({ ...data, issueDate: v })}
          />
          <Field
            label={'⏰ ' + (isAr ? 'تاريخ الاستحقاق' : "Date d'échéance")}
            type="date"
            value={data.dueDate}
            onChange={v => setData({ ...data, dueDate: v })}
          />
        </div>
        
        {/* Status */}
        <div className="mb-3">
          <label className="block text-sm font-bold text-gray-700 mb-1">
            {'📊 ' + (isAr ? 'الحالة' : 'Statut')}
          </label>
          <select
            value={data.status}
            onChange={e => setData({ ...data, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
          >
            <option value="pending">⏳ {isAr ? 'في الانتظار' : 'En attente'}</option>
            <option value="cashed">✅ {isAr ? 'مصروف' : 'Encaissé'}</option>
            <option value="bounced">⚠️ {isAr ? 'مرفوض' : 'Rejeté'}</option>
            <option value="cancelled">❌ {isAr ? 'ملغى' : 'Annulé'}</option>
          </select>
        </div>
        
        {/* Note */}
        <div className="mb-4">
          <label className="block text-sm font-bold text-gray-700 mb-1">
            {'📝 ' + (isAr ? 'ملاحظة' : 'Note')}
          </label>
          <input
            type="text"
            value={data.note}
            onChange={e => setData({ ...data, note: e.target.value })}
            placeholder={isAr ? 'مثلا: chèque للإيجار...' : 'Ex: Chèque pour location...'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        
        <div className="flex gap-2 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-bold"
          >
            {isAr ? 'إلغاء' : 'Annuler'}
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold"
          >
            💾 {isAr ? 'حفظ' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
